/**
 * Auth Event Bus
 *
 * A lightweight pub/sub for broadcasting auth-related block events from
 * anywhere (e.g., axios interceptors) to the React tree (App.tsx).
 *
 * This avoids tight coupling between the HTTP layer and React state.
 */

// ============================================================================
// Types
// ============================================================================

export type AuthBlockReason =
  | "pending_activation" // Cognito user not yet activated by supervisor
  | "unauthorized" // 401 — session expired / invalid token
  | "forbidden" // 403 — insufficient permissions
  | "payment_required" // 402 — access restricted
  | "unknown"; // Catch-all for other auth errors

export interface AuthBlockEvent {
  reason: AuthBlockReason;
  message?: string; // Optional detail from the server response
}

// eslint-disable-next-line no-unused-vars
type AuthBlockListener = (event: AuthBlockEvent) => void;

// ============================================================================
// Event Bus
// ============================================================================

const listeners = new Set<AuthBlockListener>();

/**
 * Subscribe to auth-block events.
 * Returns an unsubscribe function.
 */
export function onAuthBlocked(listener: AuthBlockListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Emit an auth-block event.
 * Called from axios interceptors (or anywhere) when an auth-related error is detected.
 */
export function emitAuthBlocked(event: AuthBlockEvent): void {
  listeners.forEach(listener => listener(event));
}
